import { memo, useCallback, useRef, useState } from "react";
import type { TextInput } from "react-native";
import { View, Text, Button } from "@src/components";
import { FormInput, phoneFormatter } from "@src/components/FormInput";

export type CreateContactData = {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
};

type ContactMode = "email" | "sms";

interface CreateContactFormProps {
  mode: ContactMode;
  onSave: (data: CreateContactData) => void;
  isSaving?: boolean;
  hideHeader?: boolean;
  InputComponent?: typeof FormInput;
}

export const CreateContactForm = memo(
  ({ mode, onSave, isSaving, hideHeader = false, InputComponent }: CreateContactFormProps) => {
    const Input = InputComponent ?? FormInput;
    const firstNameRef = useRef("");
    const lastNameRef = useRef("");
    const emailRef = useRef("");
    const phoneNumberRef = useRef("");

    const firstNameInputRef = useRef<TextInput>(null);
    const lastNameInputRef = useRef<TextInput>(null);
    const emailInputRef = useRef<TextInput>(null);
    const phoneInputRef = useRef<TextInput>(null);

    const [errors, setErrors] = useState<{ firstName?: string; email?: string; phoneNumber?: string }>({});

    const handleFirstNameChange = useCallback((value: string) => {
      firstNameRef.current = value;
      if (value.trim()) setErrors((prev) => (prev.firstName ? { ...prev, firstName: undefined } : prev));
    }, []);

    const handleLastNameChange = useCallback((value: string) => {
      lastNameRef.current = value;
    }, []);

    const handleEmailChange = useCallback((value: string) => {
      emailRef.current = value;
      if (value.trim()) setErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
    }, []);

    const handlePhoneChange = useCallback((value: string) => {
      phoneNumberRef.current = value;
      if (value.trim()) setErrors((prev) => (prev.phoneNumber ? { ...prev, phoneNumber: undefined } : prev));
    }, []);

    const handleSave = useCallback(() => {
      const firstName = firstNameRef.current;
      const lastName = lastNameRef.current;
      const email = emailRef.current;
      const phoneNumber = phoneNumberRef.current;

      const newErrors: { firstName?: string; email?: string; phoneNumber?: string } = {};

      if (mode === "email") {
        if (!email.trim()) newErrors.email = "Email address is required";
      } else {
        if (!firstName.trim()) newErrors.firstName = "First name is required";
        if (!phoneNumber.trim()) {
          newErrors.phoneNumber = "Mobile number is required";
        } else if (!/^\+\d{6,15}$/.test(phoneNumber.trim())) {
          newErrors.phoneNumber = "Use international format, e.g. +61123456789";
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      onSave({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: mode === "email" ? email.trim() : undefined,
        phoneNumber: mode === "sms" ? phoneNumber.trim() : undefined,
      });
    }, [mode, onSave]);

    return (
      <View>
        {!hideHeader && (
          <Text textStyle="md/medium" mb="md">
            Create new contact
          </Text>
        )}
        <Text textStyle="xs/regular" color="textMuted" mb="4xl">
          Add a new contact to receive notifications for this camera.
        </Text>

        <View gap="3xl">
          {mode === "email" ?
            <Input
              ref={emailInputRef}
              placeholder="Email address"
              onChangeText={handleEmailChange}
              size="medium"
              keyboardType="email-address"
              autoCapitalize="none"
              error={errors.email}
            />
          : <>
              <Input
                ref={firstNameInputRef}
                placeholder="First name"
                onChangeText={handleFirstNameChange}
                size="medium"
                autoCapitalize="words"
                error={errors.firstName}
              />
              <Input
                ref={lastNameInputRef}
                placeholder="Last name"
                onChangeText={handleLastNameChange}
                size="medium"
                autoCapitalize="words"
              />
              <Input
                ref={phoneInputRef}
                placeholder="Mobile number"
                onChangeText={handlePhoneChange}
                size="medium"
                formatter={phoneFormatter}
                error={errors.phoneNumber}
              />
            </>
          }
        </View>

        <View mt="4xl">
          <Button variant="primary" label="Save" size="md" onPress={handleSave} loading={isSaving} />
        </View>
      </View>
    );
  },
);

CreateContactForm.displayName = "CreateContactForm";
